import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { TextAreaInput, FormInputError, Tooltip } from "@toddle-edu/ds-web";
import { getWordCount } from "Utils";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import classNames from "classnames";
import classes from "./TextAreaInput.scss";
import { textareaHTMLAttributes } from "Constants";
import { WarningCircleOutlined } from "@toddle-edu/ds-icons";
class FormTextAreaInput extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.errorId = _.uniqueId("form-error-");
    this.state = {
      ...this.state,
      keysDown: {},
    };
  }

  handleKeyDown = event => {
    const kd = this.state.keysDown;
    kd[event.keyCode] = true;
    this.setState({ keysDown: kd });

    if (kd[16] && kd[13] && this.props.allowShiftEnter) {
      event.preventDefault();
      const updateVal = `${event.target.value}\r`;
      kd[event.keyCode] = false;
      this.setState({ keysDown: kd });
      this.updateValue(updateVal);
    } else if (event.key == "Enter" && this.props.onEnterPress) {
      event.preventDefault();
      this.props.onEnterPress(event.target.value);
      this.setState({ keysDown: {} });
    }
    if (this.props.onKeyDown) {
      this.props.onKeyDown(event);
    }
  };

  handleKeyUp = e => {
    if (this.props.onEnterPress) {
      const kd = this.state.keysDown;
      kd[e.keyCode] = false;
      this.setState({ keysDown: kd });
    }
    if (this.props.onKeyUp) {
      this.props.onKeyUp(e);
    }
  };

  handleBlur = e => {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    } else {
      this.onBlur({ value: e.target.value });
    }
  };

  resetErrorState = () => this.setState({ error: "" });

  handleChange = e => {
    const text = e.target.value;

    this.setState({ error: "" });
    this.updateValue(text);
  };

  renderEdit = () => {
    const {
      value,
      maxLength,
      textAreaStyle,
      textAreaStyles,
      showWordCount,
      shouldErrorAlignRight,
      t,
      showMaxLength,
      customErrorClass,
      testId = `uiComponent:formTextAreaInput`,
      dsVersion,
      "aria-describedby": ariaDescribedBy,
      invalidErrorMessage,
      ...rest
    } = this.props;
    // Here, if you pass showWordCount:true then maxLength will be considered as length of words.
    // otherwise maxLength will be considered as character length

    const { error } = this.state;

    const stringValue = String(!_.isEmpty(value) ? value : "");

    const currentTextLength = showWordCount
      ? getWordCount({ str: stringValue })
      : stringValue.length;

    const showWarningText = currentTextLength > maxLength;

    const maxLengthContainerClasses = classNames(
      "text-label-3 text-textSubtle",
      {
        invisible: !maxLength,
      },
      classes.maxLengthContainer,
      { [classes.warningText]: showWarningText }
    );

    const containerClass = classNames("w-full", classes.containerClass);
    const errorClass = classNames({
      ["flex justify-between"]: true,
      [classes.errorClass]: true,
      [classes.alignRight]: shouldErrorAlignRight,
      [customErrorClass]: !_.isEmpty(customErrorClass),
    });

    return (
      <div className={containerClass}>
        <TextAreaInput
          {..._.pick(rest, textareaHTMLAttributes)}
          label={""}
          value={value ? value : ""}
          ref={this.updateInputRef}
          onChange={this.handleChange}
          onKeyDown={e => this.handleKeyDown(e)}
          onKeyUp={e => this.handleKeyUp(e)}
          onBlur={e => this.handleBlur(e)}
          onFocus={e => this.onFocus({ value: e.target.value })}
          error={error || invalidErrorMessage}
          style={{
            ...textAreaStyle,
            ...textAreaStyles,
            unicodeBidi: "plaintext",
          }}
          testId={testId}
          dsVersion={dsVersion}
          aria-describedby={ariaDescribedBy ?? this.errorId}
        />
        <div className={errorClass}>
          <FormInputError
            id={this.errorId}
            {...(!_.isEmpty(error) && { className: "flex-1 mr-4", error })}
          />
          {showMaxLength ? (
            <span className={maxLengthContainerClasses}>
              {showWarningText ? (
                <Tooltip
                  tooltip={t("common:max_limit_count_label", {
                    count: maxLength,
                    label: showWordCount
                      ? t("common:word", {
                          count: maxLength,
                        })
                      : t("common:character", {
                          count: maxLength,
                        }),
                  })}
                  placement={"bottom"}
                  showArrow={true}
                >
                  <span className={classes.warningContainer}>
                    <WarningCircleOutlined
                      size={"xxx-small"}
                      variant={"critical"}
                    />
                    <span>
                      {currentTextLength}/{maxLength}
                    </span>
                  </span>
                </Tooltip>
              ) : (
                <span>
                  {currentTextLength}/{maxLength}
                </span>
              )}
            </span>
          ) : null}
        </div>
      </div>
    );
  };
}

FormTextAreaInput.defaultProps = {
  ...UIBaseComponent.defaultProps,
  textAreaStyles: {},
  minRows: 3,
  allowShiftEnter: false,
  showMaxCharacterLength: false,
  autoFocus: false,
  showWordCount: false,
  shouldErrorAlignRight: false,
  showMaxLength: true,
  dsVersion: "1.0",
};

FormTextAreaInput.propTypes = {
  ...UIBaseComponent.propTypes,
  showMaxLength: PropTypes.bool,
  maxRows: PropTypes.number,
  minRows: PropTypes.number,
  maxLength: PropTypes.number,
  textAreaStyles: PropTypes.object,
  onEnterPress: PropTypes.func,
  allowShiftEnter: PropTypes.bool,
  inputRef: PropTypes.func,
  autoFocus: PropTypes.bool,
  onKeyUp: PropTypes.func,
  onKeyDown: PropTypes.func,
  onPaste: PropTypes.func,
  onBlur: PropTypes.func,
  showWordCount: PropTypes.bool,
  shouldErrorAlignRight: PropTypes.bool,
  dsVersion: PropTypes.string,
};

export default FormTextAreaInput;
